// Shrinks reference images so a generation request survives the ingress in front
// of the API.
//
// Why this exists: inline references travel as base64 inside the JSON body of
// POST /api/asset-lab/ai/generations/, and the nginx ingress caps the request
// body long before FastAPI's own "max size is 5 MB" check ever runs. A body over
// the cap comes back as nginx's own HTML "413 Request Entity Too Large" — no API
// JSON, no `detail` (see describeAiError, which special-cases it). base64 costs
// +33% on top of the file, so a plain 1.6 MB PNG screenshot is already ~2.1 MB on
// the wire and gets rejected.
//
// The direct-to-Gemini path used to sidestep this entirely by uploading refs to
// Google's Files API from the browser (providers/filesApi.ts) — our proxy has no
// equivalent yet, so until it does (a multipart reference endpoint, or raising
// the ingress cap) the client has to make the payload fit.
//
// Re-encoding is lossy, so it is applied only to what doesn't fit: a reference
// already within its share of the budget is sent byte-for-byte as before.

// What the ingress accepts for one request. Assumed to be the ingress-nginx
// default — the observed 413 on a ~2.1 MB body is consistent with it, and no
// `proxy-body-size` annotation is set for this route. Raising it server-side is
// the real fix; then only this constant moves.
const REQUEST_BODY_LIMIT_BYTES = 1024 * 1024;

// Decoded bytes all inline references together may occupy. base64 inflates by
// 4/3, and the rest of the payload (prompt, model, JSON syntax) has to fit too,
// hence the headroom rather than the bare limit / 1.34.
export const INLINE_BUDGET_BYTES = Math.floor((REQUEST_BODY_LIMIT_BYTES / 4) * 3) - 128 * 1024;

// Longest side of a re-encoded reference. The models rescale their input to
// roughly this anyway (and Gemini bills input images by size), so going above it
// costs payload and tokens without reaching the model as extra detail. Matches
// MAX_CAPTURE_DIM in capture.ts.
const MAX_DIMENSION = 1536;

// Tried in order until the result fits its share of the budget: quality first at
// full allowed size, then progressively smaller canvases. Keeping pixels and
// dropping quality preserves composition, which is what a reference is for.
const DIMENSION_STEPS = [MAX_DIMENSION, 1024, 768, 512];
const QUALITY_STEPS = [0.85, 0.7, 0.55];

// Lossy WebP is the target format because it keeps the alpha channel: Asset Lab
// references are routinely cut-outs, logos and Remove-background results, and
// JPEG would flatten their transparency to black — an artefact the model would
// then faithfully reproduce. Both Gemini and OpenAI accept image/webp. JPEG is
// the fallback for a canvas that declines to encode WebP (it silently produces
// PNG instead, which would defeat the point).
const PREFERRED_TYPE = 'image/webp';
const FALLBACK_TYPE = 'image/jpeg';

function fitWithin(w: number, h: number, max: number): { w: number; h: number } {
  if (w <= max && h <= max) return { w, h };
  const scale = Math.min(max / w, max / h);
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}

// Draws the bitmap at the given size and encodes it. OffscreenCanvas keeps the
// work off the DOM; a plain <canvas> is the fallback for browsers without
// convertToBlob. Returns null when encoding fails or yields another format than
// the one asked for (the canvas fallback for an unsupported type).
async function encode(
  bitmap: ImageBitmap,
  w: number,
  h: number,
  type: string,
  quality: number,
): Promise<Blob | null> {
  let blob: Blob | null = null;

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    blob = await canvas.convertToBlob({ type, quality });
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((out) => resolve(out), type, quality);
    });
  }

  return blob && blob.type === type ? blob : null;
}

// Smallest encoding of `blob` that fits `targetBytes`, or — when nothing fits —
// the smallest one produced, so the request goes out as light as we can make it
// instead of being abandoned here. Null when the image can't be decoded or
// encoded at all, meaning the caller should keep the original bytes.
async function shrink(blob: Blob, targetBytes: number): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null; // not decodable here (exotic format) — leave it to the server
  }

  try {
    let best: Blob | null = null;
    for (const dimension of DIMENSION_STEPS) {
      const { w, h } = fitWithin(bitmap.width, bitmap.height, dimension);
      for (const quality of QUALITY_STEPS) {
        const out =
          (await encode(bitmap, w, h, PREFERRED_TYPE, quality)) ??
          (await encode(bitmap, w, h, FALLBACK_TYPE, quality));
        if (!out) return best; // encoding unsupported outright — stop trying
        if (!best || out.size < best.size) best = out;
        if (out.size <= targetBytes) return out;
      }
      // Every quality at this size overshot: the next, smaller canvas is the
      // only way down. Re-encoding at a size we already passed is pointless.
    }
    return best;
  } finally {
    bitmap.close();
  }
}

// One reference's bytes on the way to the provider, before base64.
export type InlineBlob = { blob: Blob; name?: string };

export type FittedInline = {
  blobs: Blob[];
  // Names of the references that had to be re-encoded, in input order — the
  // modal warns with these so a lossy send is never silent.
  compressed: string[];
};

// Fits a set of inline references into INLINE_BUDGET_BYTES, re-encoding only
// those that need it.
//
// The budget is shared, not per-image: each reference is allowed its fair share
// of what is left, smallest first, so the unused remainder of a small reference
// is handed to the larger ones instead of being wasted. With one reference that
// is the whole budget; with eight it is an eighth each, which is what makes a
// full basket of screenshots send at all.
export async function fitInlineBlobs(items: InlineBlob[]): Promise<FittedInline> {
  const total = items.reduce((sum, item) => sum + item.blob.size, 0);
  if (total <= INLINE_BUDGET_BYTES) {
    return { blobs: items.map((item) => item.blob), compressed: [] };
  }

  // Smallest first, so a reference that already fits releases its unused share
  // to the ones still to be processed.
  const order = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.blob.size - b.item.blob.size);

  const blobs: Blob[] = new Array<Blob>(items.length);
  const compressedByIndex = new Array<boolean>(items.length).fill(false);
  let remaining = INLINE_BUDGET_BYTES;
  let left = order.length;

  for (const { item, index } of order) {
    const share = Math.floor(remaining / left);
    left -= 1;
    if (item.blob.size <= share) {
      blobs[index] = item.blob;
      remaining -= item.blob.size;
      continue;
    }
    const shrunk = await shrink(item.blob, share);
    // A failed re-encode keeps the original: the server may still accept it, and
    // dropping a reference the user explicitly picked is worse than trying.
    blobs[index] = shrunk ?? item.blob;
    compressedByIndex[index] = !!shrunk;
    remaining -= blobs[index].size;
  }

  return {
    blobs,
    compressed: items
      .map((item, index) => (compressedByIndex[index] ? item.name || 'reference' : null))
      .filter((name): name is string => name !== null),
  };
}
